import * as React from "react";

import { cn } from "@/lib/utils";
import { Icon } from "./Icon";
import { EmptyState } from "./EmptyState";

export interface DataTableColumn<T> {
  key: string;
  header: React.ReactNode;
  /**
   * Track de la grilla: `"minmax(220px,1fr)"`, `"150px"`, `"116px"`.
   * La cabecera y las filas comparten esta definición — por eso las columnas
   * quedan alineadas sin que haya un `<table>` de por medio.
   */
  width: string;
  align?: "left" | "right" | "center";
  cell: (row: T, index: number) => React.ReactNode;
  /** Oculta el encabezado (columna de acciones al final de la fila). */
  srOnlyHeader?: boolean;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T, index: number) => React.Key;
  /** Ancho mínimo compartido: por debajo, scrollea el contenedor. */
  minWidth?: number;
  /** 9px por defecto (compacta) o 13px (cómoda). */
  density?: "compacta" | "comoda";
  /** Filtros a la izquierda, acción a la derecha. */
  toolbar?: React.ReactNode;
  /** Qué mostrar cuando no hay filas. */
  emptyMessage?: string;
  emptyIcon?: string;
  /**
   * Acción discreta dentro del vacío — "Quitar filtros" y poco más. El vacío
   * informa, no vende: `EmptyState` ya limita el tamaño de lo que entra acá.
   */
  emptyAction?: React.ReactNode;
  onRowClick?: (row: T) => void;
  /** Detalle corto: expande la fila en lugar de navegar a otra pantalla. */
  renderExpanded?: (row: T) => React.ReactNode;
  /** Paginación de 20 en 20. Pasá `false` para desactivarla. */
  pageSize?: number | false;
  className?: string;
}

const ALIGN: Record<NonNullable<DataTableColumn<unknown>["align"]>, string> = {
  left: "justify-start text-left",
  right: "justify-end text-right",
  center: "justify-center text-center",
};

/**
 * Tabla del Design System — el componente central de la app.
 *
 * CSS grid, NO `<table>`: la cabecera y las filas comparten
 * `grid-template-columns` y `min-width`, y el scroll horizontal vive en el
 * contenedor. Así se mantiene la densidad y el control de columnas.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  minWidth = 700,
  density = "compacta",
  toolbar,
  emptyMessage = "No hay nada que mostrar todavía.",
  emptyIcon,
  emptyAction,
  onRowClick,
  renderExpanded,
  pageSize = 20,
  className,
}: DataTableProps<T>) {
  const [page, setPage] = React.useState(0);
  const [abiertas, setAbiertas] = React.useState<Set<React.Key>>(new Set());

  const porPagina = pageSize === false ? rows.length : pageSize;
  const totalPaginas =
    pageSize === false ? 1 : Math.max(1, Math.ceil(rows.length / porPagina));

  // Si el filtro achica el listado, la página actual puede quedar fuera de rango.
  React.useEffect(() => {
    setPage((p) => Math.min(p, totalPaginas - 1));
  }, [totalPaginas]);

  const visibles =
    pageSize === false
      ? rows
      : rows.slice(page * porPagina, page * porPagina + porPagina);

  const gridTemplate = React.useMemo(
    () =>
      (renderExpanded ? "26px " : "") + columns.map((c) => c.width).join(" "),
    [columns, renderExpanded]
  );

  const padY = density === "compacta" ? "py-[9px]" : "py-[13px]";

  const toggle = (key: React.Key) =>
    setAbiertas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const desde = rows.length === 0 ? 0 : page * porPagina + 1;
  const hasta = Math.min(rows.length, (page + 1) * porPagina);

  return (
    // `bg-card` NO es decorativo: el área de contenido es `bg-canvas` (#F2F4F6)
    // y esta tarjeta traía borde pero ningún fondo, así que las filas se
    // apoyaban directo sobre el gris del lienzo. El Design System dice que el
    // contenedor de datos es blanco — la cabecera y el pie sí son grises
    // (`bg-surface-muted`), y ese contraste es justamente lo que separa los
    // datos de su chrome. Lo arrastraban las 9 pantallas que usan DataTable.
    <div className={cn("bg-card overflow-x-auto rounded-2xl border", className)}>
      <div style={{ minWidth }}>
        {toolbar && (
          // flex-wrap: una toolbar con segmented + buscador + tres selects no
          // entra en una línea, y sin envolver los selects se comprimen hasta
          // dejar de leerse. Misma regla que las grillas del DS: envolver antes
          // que comprimir.
          <div className="border-border-subtle flex flex-wrap items-center gap-2.5 border-b px-3.5 py-3">
            {toolbar}
          </div>
        )}

        {/* Cabecera */}
        <div
          role="row"
          className="bg-surface-muted border-border-subtle fc-th grid h-9 items-center border-b px-3.5"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {renderExpanded && <span />}
          {columns.map((col) => (
            <div
              key={col.key}
              role="columnheader"
              className={cn("flex items-center", ALIGN[col.align ?? "left"])}
            >
              {/*
                `sr-only` va ADENTRO de la celda, nunca sobre ella: es
                `position:absolute`, así que puesto en el hijo directo del grid
                saca la celda del flujo y la grilla recorre una posición TODAS
                las columnas siguientes — la cabecera deja de estar alineada
                con las filas. Con la columna oculta al final no se notaba;
                con una al principio (un checkbox de selección) desalinea la
                tabla entera.
              */}
              {col.srOnlyHeader ? <span className="sr-only">{col.header}</span> : col.header}
            </div>
          ))}
        </div>

        {/* Filas */}
        {visibles.length === 0 ? (
          <EmptyState mensaje={emptyMessage} icono={emptyIcon} accion={emptyAction} />
        ) : (
          visibles.map((row, i) => {
            const key = getRowKey(row, i);
            const abierta = abiertas.has(key);
            return (
              <div key={key} className="border-border-faint border-b last:border-b-0">
                <div
                  role="row"
                  onClick={
                    renderExpanded
                      ? () => toggle(key)
                      : onRowClick
                        ? () => onRowClick(row)
                        : undefined
                  }
                  className={cn(
                    "hover:bg-primary-50 grid items-center px-3.5 transition-colors",
                    padY,
                    (onRowClick || renderExpanded) && "cursor-pointer"
                  )}
                  style={{ gridTemplateColumns: gridTemplate }}
                >
                  {renderExpanded && (
                    <Icon
                      name="chevron_right"
                      size={16}
                      className={cn(
                        "text-soft transition-transform",
                        abierta && "rotate-90"
                      )}
                    />
                  )}
                  {columns.map((col) => (
                    <div
                      key={col.key}
                      role="cell"
                      className={cn(
                        "flex min-w-0 items-center",
                        ALIGN[col.align ?? "left"]
                      )}
                    >
                      {col.cell(row, i)}
                    </div>
                  ))}
                </div>
                {renderExpanded && abierta && (
                  <div className="bg-surface-muted border-border-faint animate-fc-in border-t px-3.5 py-3">
                    {renderExpanded(row)}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Pie */}
        {pageSize !== false && rows.length > 0 && (
          <div className="bg-surface-muted border-border-subtle flex items-center gap-3 border-t px-3.5 py-2.5">
            <span className="text-meta text-xs">
              Mostrando <span className="fc-num">{desde}</span>–
              <span className="fc-num">{hasta}</span> de{" "}
              <span className="fc-num">{rows.length}</span>
            </span>
            <div className="flex-1" />
            {totalPaginas > 1 && (
              <>
                <span className="text-meta text-xs">
                  Página <span className="fc-num">{page + 1}</span> de{" "}
                  <span className="fc-num">{totalPaginas}</span>
                </span>
                <div className="flex gap-0.5">
                  <PageButton
                    icon="chevron_left"
                    label="Página anterior"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  />
                  <PageButton
                    icon="chevron_right"
                    label="Página siguiente"
                    disabled={page >= totalPaginas - 1}
                    onClick={() =>
                      setPage((p) => Math.min(totalPaginas - 1, p + 1))
                    }
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PageButton({
  icon,
  label,
  disabled,
  onClick,
}: {
  icon: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="border-input bg-background text-body hover:bg-border-faint flex size-[30px] items-center justify-center rounded-md border transition-colors disabled:pointer-events-none disabled:opacity-35"
    >
      <Icon name={icon} size={17} />
    </button>
  );
}

/**
 * Celda principal en dos líneas: 13px/500 arriba y metadato de 11px abajo,
 * ambas truncadas. Es el patrón por defecto de la primera columna.
 */
export function CeldaPrincipal({
  titulo,
  sufijoTitulo,
  meta,
  icono,
  className,
}: {
  titulo: React.ReactNode;
  /**
   * Indicador pegado al título (nivel, marca, contador). Va FUERA del span
   * que trunca: si viviera dentro, un título largo se lo comería con los
   * puntos suspensivos justo cuando la fila más necesita distinguirse.
   */
  sufijoTitulo?: React.ReactNode;
  meta?: React.ReactNode;
  /** Glifo que acompaña al metadato. */
  icono?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex min-w-0 flex-col gap-0.5 pr-4", className)}>
      {sufijoTitulo ? (
        <span className="flex min-w-0 items-center gap-2">
          <span className="fc-cell fc-truncate">{titulo}</span>
          <span className="flex shrink-0 items-center">{sufijoTitulo}</span>
        </span>
      ) : (
        <span className="fc-cell fc-truncate">{titulo}</span>
      )}
      {meta && (
        <span className="flex min-w-0 items-center gap-1.5">
          {icono && <Icon name={icono} size={14} className="text-icon-soft" />}
          <span className="fc-meta fc-truncate">{meta}</span>
        </span>
      )}
    </div>
  );
}

/** Separador elástico para la toolbar: filtros | acción. */
export function ToolbarSpacer() {
  return <div className="flex-1" />;
}
